import { useTranslations } from '@/hooks/useTranslations'
import type {
  Control,
  FieldArrayWithId,
  UseFieldArrayAppend,
  UseFieldArrayRemove,
} from 'react-hook-form'

import { BillLineItemsGrid } from '@/components/pages/Admin/BillManagement/Form/BillLineItemsGrid'
import type { BillLineItemFormValue } from '@/components/pages/Admin/BillManagement/Form/billLineItemsShared'
import { formatNumber } from '@/utils/number'
import { cn } from '@/lib/utils'

const COL_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'] as const

function SheetSectionHeader({ rowNumber, title }: { rowNumber: number; title: string }) {
  return (
    <tr>
      <td className="w-10 border border-border/70 bg-muted/40 px-1.5 py-0 text-center text-xs text-muted-foreground">
        {rowNumber}
      </td>
      <td
        colSpan={6}
        className="border border-border/70 bg-primary/10 px-2 py-1.5 text-xs font-bold uppercase tracking-wide text-foreground"
      >
        {title}
      </td>
    </tr>
  )
}

function SheetSummaryRow({
  rowNumber,
  label,
  value,
  unit,
  emphasize,
}: {
  rowNumber: number
  label: string
  value: string
  unit: string
  emphasize?: boolean
}) {
  return (
    <tr className={cn(emphasize && 'bg-primary/5')}>
      <td className="w-10 border border-border/70 bg-muted/40 px-1.5 py-0 text-center text-xs text-muted-foreground">
        {rowNumber}
      </td>
      <td className="border border-border/70 px-2 py-1.5 text-xs font-medium">{label}</td>
      <td
        colSpan={5}
        className={cn(
          'border border-border/70 px-2 py-1.5 text-right text-sm tabular-nums',
          emphasize && 'font-bold',
        )}
      >
        {value} <span className="text-muted-foreground">{unit}</span>
      </td>
    </tr>
  )
}

type BillFormExcelOrderSectionProps = {
  control: Control<any>
  lineItemFields: FieldArrayWithId<any, 'line_items', 'id'>[]
  appendLineItem: UseFieldArrayAppend<any, 'line_items'>
  removeLineItem: UseFieldArrayRemove
  lineItems: BillLineItemFormValue[] | undefined
  isWholesaleSell: boolean
  syncWholesaleRowWeight: (rowIndex: number) => void
  disabled?: boolean
  totalKg: number
  subtotalBaht: number
  extraChargeTotal: number
  discountWatch: number
  isWholesaleSellSummary: boolean
  netBaht: number
  lineItemsErrorMessage?: string
  hasLineItemsError: boolean
}

/** Excel-sheet block for order line items + bill summary (shared by Excel + Hybrid). */
export function BillFormExcelOrderSection({
  control,
  lineItemFields,
  appendLineItem,
  removeLineItem,
  lineItems,
  isWholesaleSell,
  syncWholesaleRowWeight,
  disabled,
  totalKg,
  subtotalBaht,
  extraChargeTotal,
  discountWatch,
  isWholesaleSellSummary,
  netBaht,
  lineItemsErrorMessage,
  hasLineItemsError,
}: BillFormExcelOrderSectionProps) {
  const t = useTranslations('admin.bill_management.form')

  let row = 1
  const nextRow = () => {
    const current = row
    row += 1
    return current
  }

  const orderHeaderRow = nextRow()
  const orderBodyRow = nextRow()
  const summaryHeaderRow = nextRow()
  const summaryKgRow = nextRow()
  const summarySubtotalRow = nextRow()
  const summaryExtraRow = isWholesaleSellSummary ? nextRow() : null
  const summaryDiscountRow = isWholesaleSellSummary ? nextRow() : null
  const summaryNetRow = nextRow()

  return (
    <div className="overflow-x-auto rounded-md border border-border/80 bg-[#fafafa] shadow-sm dark:bg-background">
      <table className="min-w-[56rem] w-full border-collapse text-sm">
        <thead>
          <tr>
            <th className="w-10 border border-border/70 bg-muted/60 px-1.5 py-1 text-center text-[10px] font-semibold text-muted-foreground" />
            {COL_LETTERS.map((letter) => (
              <th
                key={letter}
                className="border border-border/70 bg-muted/60 px-1.5 py-1 text-center text-[10px] font-semibold text-muted-foreground"
              >
                {letter}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <SheetSectionHeader rowNumber={orderHeaderRow} title={t('order_information.title')} />
          <tr>
            <td className="w-10 border border-border/70 bg-muted/40 px-1.5 py-0 text-center text-xs text-muted-foreground">
              {orderBodyRow}
            </td>
            <td
              colSpan={6}
              className={cn(
                'border border-border/70 p-0',
                hasLineItemsError && 'ring-2 ring-inset ring-destructive/40',
              )}
            >
              <BillLineItemsGrid
                control={control}
                fields={lineItemFields}
                append={appendLineItem}
                remove={removeLineItem}
                lineItems={lineItems}
                isWholesaleSell={isWholesaleSell}
                syncWholesaleRowWeight={syncWholesaleRowWeight}
                disabled={disabled}
                variant="excel"
              />
              {lineItemsErrorMessage ? (
                <p className="border-t border-destructive/30 px-2 py-1.5 text-xs font-medium text-destructive">
                  {lineItemsErrorMessage}
                </p>
              ) : null}
            </td>
          </tr>

          <SheetSectionHeader rowNumber={summaryHeaderRow} title="สรุปทั้งบิล" />
          <SheetSummaryRow
            rowNumber={summaryKgRow}
            label="น้ำหนักรวม"
            value={formatNumber(totalKg)}
            unit="กก."
          />
          <SheetSummaryRow
            rowNumber={summarySubtotalRow}
            label="ยอดทุเรียน"
            value={formatNumber(subtotalBaht)}
            unit="บาท"
          />
          {summaryExtraRow != null ? (
            <SheetSummaryRow
              rowNumber={summaryExtraRow}
              label="ค่าใช้จ่ายเพิ่ม"
              value={formatNumber(extraChargeTotal)}
              unit="บาท"
            />
          ) : null}
          {summaryDiscountRow != null ? (
            <SheetSummaryRow
              rowNumber={summaryDiscountRow}
              label="หักส่วนลด"
              value={formatNumber(discountWatch)}
              unit="บาท"
            />
          ) : null}
          <SheetSummaryRow
            rowNumber={summaryNetRow}
            label="ยอดสุทธิ (รวมภาษี 0%)"
            value={formatNumber(netBaht)}
            unit="บาท"
            emphasize
          />
        </tbody>
      </table>
      <p className="border-t border-border/70 bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
        ตารางรายการแบบ Excel · Tab เลื่อนเซลล์ · Enter ลงแถวถัดไป
      </p>
    </div>
  )
}
